/**
 * Gastseite zur Anfrage (`/anfrage/:token`, KEIN Login) — Mockup v2 der Eingangsmail, freigegeben
 * von Antoine am 06.10.2026. Der Gast sieht seine Angaben und schickt eine Frage oder Korrektur;
 * sie landet als eingehende Nachricht am Vorgang (sql/674). Keine Beträge. `?korrektur=1` (Link
 * „Hier korrigieren“ in der Mail) öffnet die Seite gleich im Korrekturmodus.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Icon, Button, Textarea } from "../ui/components";
import { GastRahmen } from "../ui/BetriebsKopf";

interface GastseiteData {
  betrieb: string | null;
  anlass: string | null;
  datum: string | null;
  uhrzeit: string | null;
  gaeste: number | null;
  name: string | null;
  firma: string | null;
  email: string | null;
  telefon: string | null;
  nachricht: string | null;
}

type Phase = "loading" | "ready" | "submitting" | "done" | "error";
type Art = "frage" | "korrektur";

function datumDe(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function GastAnfragePage() {
  const { token } = useParams<{ token: string }>();
  const [suche] = useSearchParams();
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GastseiteData | null>(null);
  const [art, setArt] = useState<Art>(suche.get("korrektur") === "1" ? "korrektur" : "frage");
  const [text, setText] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/anfrage/${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error(res.status === 404 ? "Dieser Link ist nicht (mehr) gültig." : "Fehler beim Laden.");
        setData(((await res.json()) as { data: GastseiteData }).data);
        setPhase("ready");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("error");
      }
    })();
  }, [token]);

  async function senden() {
    if (!token || text.trim().length < 2) return;
    setPhase("submitting");
    setError(null);
    try {
      const res = await fetch(`/api/public/anfrage/${encodeURIComponent(token)}/nachricht`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ art, text: text.trim() }),
      });
      if (!res.ok) throw new Error(res.status === 429 ? "Zu viele Nachrichten in kurzer Zeit. Bitte versuchen Sie es gleich noch einmal." : "Die Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.");
      setPhase("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("ready");
    }
  }

  const angaben = data
    ? [
        data.anlass,
        [datumDe(data.datum), data.uhrzeit ? `${data.uhrzeit} Uhr` : null].filter(Boolean).join(", ") || null,
        data.gaeste ? `${data.gaeste} Gäste` : null,
        [data.name, data.firma].filter(Boolean).join(", ") || null,
        [data.email, data.telefon].filter(Boolean).join(" · ") || null,
      ].filter((z): z is string => Boolean(z))
    : [];

  return (
    <GastRahmen name={data?.betrieb}>
      {phase === "loading" && <p className="text-center font-body-sm text-body-sm text-secondary">Lädt …</p>}
      {phase === "error" && error && (
        <p className="rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{error}</p>
      )}
      {phase === "done" && (
        <div className="text-center">
          <Icon name="check_circle" className="mx-auto mb-3 text-[40px] text-tertiary dark:text-tertiary-fixed-dim" />
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Vielen Dank!</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {art === "korrektur" ? "Ihre Korrektur ist bei uns angekommen. Wir berücksichtigen sie im Angebot." : "Ihre Nachricht ist bei uns angekommen. Wir berücksichtigen sie im Angebot."}
          </p>
        </div>
      )}
      {(phase === "ready" || phase === "submitting") && data && (
        <>
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Ihre Anfrage</h1>
          {angaben.length > 0 && (
            <div className="mt-4 rounded-xl bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:bg-white/5 dark:text-inverse-on-surface">
              {angaben.map((z) => (
                <div key={z}>{z}</div>
              ))}
            </div>
          )}
          <div className="mt-6 flex gap-2" role="group" aria-label="Art der Nachricht">
            <Button type="button" variant={art === "frage" ? "primary" : "secondary"} aria-pressed={art === "frage"} onClick={() => setArt("frage")}>
              Etwas ergänzen
            </Button>
            <Button type="button" variant={art === "korrektur" ? "primary" : "secondary"} aria-pressed={art === "korrektur"} onClick={() => setArt("korrektur")}>
              Angaben korrigieren
            </Button>
          </div>
          <label className="mt-4 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" htmlFor="gastseite-text">
            {art === "korrektur"
              ? "Was stimmt nicht? Schreiben Sie uns einfach, wie es richtig ist."
              : "Gibt es Vegetarier oder Allergien in Ihrer Gruppe, oder möchten Sie uns noch etwas mitteilen?"}
          </label>
          <Textarea id="gastseite-text" className="mt-2 w-full" rows={5} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
          {error && (
            <p className="mt-4 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{error}</p>
          )}
          <Button type="button" className="mt-6 w-full !py-4" disabled={text.trim().length < 2 || phase === "submitting"} onClick={() => void senden()}>
            {phase === "submitting" ? "Wird gesendet …" : "Senden"}
          </Button>
        </>
      )}
    </GastRahmen>
  );
}
